<template>
  <div class="base-show-more">
    <div
      :style="heightStyle"
      :class="{
        'no-overflow': !animating || !expanded,
        'fade-bottom-to-white': fade && !expanded,
      }"
      class="expand"
      style="max-height: none"
    >
      <div ref="height" class="tw-inline">
        <slot name="default" :toggle="toggle" :expand="expand" :expanded="expanded" />
      </div>
    </div>
    <div v-if="seenBaseShowMoreButton" @click="toggle">
      <slot name="button" :button-text="buttonText">
        <!-- Default button if none is provided -->
        <BaseShowMoreButton
          :align-with-text="alignWithText"
          :button-text="buttonText"
          :expanded="expanded"
        />
      </slot>
    </div>
  </div>
</template>

<script>
import BaseShowMoreButton from '@/components/BaseShowMoreButton.vue'

export default {
  components: {
    BaseShowMoreButton,
  },
  props: {
    collapsedHeight: {
      type: Number,
      default: 285,
      required: false,
    },
    // Delay in ms.
    // Sometimes a delay is necessary in order to detect height correctly within modals
    delay: {
      type: Number,
      default: 0,
      required: false,
    },
    mobileOnly: {
      type: Boolean,
      default: false,
    },
    fade: {
      type: Boolean,
      default: false,
      required: false,
    },
    collapsable: {
      type: Boolean,
      default: false,
      required: false,
    },
    expandText: {
      type: String,
      default: $t('Show more'),
      required: false,
    },
    collapseText: {
      type: String,
      default: $t('Show less'),
      required: false,
    },
    startCollapsed: {
      type: Boolean,
      default: false,
    },
    // Often when using BaseShowMore, the button text should be aligned with text content above and use a negative
    // left margin to account for padding within WdsButtonGhost. This prop provides a quick way to left align.
    alignWithText: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      animating: false,
      needsCollapsing: false,
      expanded: false,
      clicked: false,
    }
  },
  computed: {
    expandHeight() {
      return this.$refs.height.offsetHeight
    },
    isMobile() {
      return window.innerWidth < 768 // from bootstrap 3 $screen-sm breakpoint
    },
    heightStyle() {
      let h
      // dont mess with height if its mobile only and the user is on desktop
      if (this.mobileOnly && !this.isMobile) {
        h = 'initial'
        // before the animation
      } else if (!this.animating) {
        h = `${this.collapsedHeight}px`
        // during the animation
      } else if (this.animating && !this.expanded) {
        h = `${this.expandHeight}px`
        // after the animation: (safari fix) given a fixed px val, divs dont stretch to contain nested content
      } else if (this.animating && this.expanded) {
        h = `initial`
      }
      return { height: h }
    },
    seenBaseShowMoreButton() {
      if (!this.needsCollapsing) {
        return false
      }
      return this.mobileOnly
        ? this.isMobile && !this.animating
        : !this.animating || this.collapsable
    },
    buttonText() {
      let t = ''
      if (!this.animating) {
        t = this.expandText
      } else if (this.collapsable) {
        t = this.collapseText
      }
      return t
    },
  },
  mounted() {
    // The slot elements need to fully render before we do our hide logic. Since we are in the same rendering tick as
    // the slot we wait our turn for the next thick by which our slot should have finished rendering
    this.$nextTick(() => {
      this.needsCollapsing = this.expandHeight > this.collapsedHeight || this.startCollapsed

      // Our slotted elements might want to extract any of our functions we supply them with in the template.
      const slotMountVars = { toggle: this.toggle, expand: this.expand }

      // Checking if any elements are in the slot can help find a potential issue where we have been mounted
      // before our slot has had its content filled out
      this.expanded =
        !this.needsCollapsing && this.$slots.default && this.$slots.default(slotMountVars).length
      if (!this.needsCollapsing) this.toggle()

      // Create the observer (and what to do on changes...)
      this.observer = new MutationObserver((_) => {
        if (!this.clicked) {
          this.needsCollapsing = this.expandHeight > this.collapsedHeight
          // Checking if any elements are in the slot can help find a potential issue where we have been mounted
          // before our slot has had its content filled out
          this.expanded =
            !this.needsCollapsing &&
            this.$slots.default &&
            this.$slots.default(slotMountVars).length
          if (!this.needsCollapsing) this.toggle()
        }
      })

      // Setup the observer
      this.observer.observe(this.$el, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      })
    })
  },
  beforeUnmount() {
    // Clean up
    this.observer.disconnect()
  },
  methods: {
    toggleClick() {
      this.clicked = true
      this.toggle()
    },
    toggle() {
      // check if its collapsed
      if (!this.animating) {
        this.expand()
      } else {
        this.collapse()
      }
    },
    expand() {
      this.animating = true
      this.clicked = true
      setTimeout((_) => {
        this.expanded = true
      }, 300)
    },
    collapse() {
      this.expanded = false
      setTimeout((_) => {
        this.animating = false
      }, 100)
    },
  },
}
</script>

<style lang="scss" scoped>
.base-show-more {
  position: relative;
}

/* Buttons within component overflow visible on click for waves-effect */
.btn[aria-expanded='true'] {
  overflow: visible;
}

.expand {
  transition: height 0.3s;
  height: 0;
}

.fade-bottom-to-white {
  position: relative;
}

.fade-bottom-to-white::after {
  content: '';
  position: absolute;
  z-index: 1;
  bottom: 0;
  left: 0;
  pointer-events: none;
  background-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 60%,
    rgba(255, 255, 255, 1) 100%
  );
  width: 100%;
  height: 4em;
}

/*
 * This little trick delays the overflow:visible
 * without it you see the text behind de "show more/show less" button while the text is expanding/collapsing
*/

.no-overflow {
  overflow: hidden;
  animation: 0.3s delay-overflow;
}

@keyframes delay-overflow {
  from {
    overflow: visible;
  }
}
</style>
